import { useEffect, useState } from 'react';
import { Download, Smartphone, X, Share } from 'lucide-react';

/**
 * InstallAppButton — Prompt to install the exercise app on the phone's home screen.
 *
 * Behaviour:
 * - On Android / Chrome / Edge: uses the native `beforeinstallprompt` event
 *   to trigger a one-tap install.
 * - On iOS Safari: shows a manual instruction card ("Partager → Sur l'écran
 *   d'accueil") because iOS does not expose the install prompt event.
 * - Hides itself entirely when the app is already running in standalone mode
 *   (i.e. it has already been installed) or after the user dismisses it.
 */
const DISMISS_KEY = 'pwa_install_dismissed_at';
const DISMISS_HIDE_DAYS = 14;

export default function InstallAppButton() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Already installed (standalone) → do nothing
    const isStandalone =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (isStandalone) return;

    // Recently dismissed → hide for 14 days
    const dismissed = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissed && Date.now() - dismissed < DISMISS_HIDE_DAYS * 24 * 3600 * 1000) return;

    // iOS detection (Safari never fires beforeinstallprompt)
    const iosLike =
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iosLike);
    if (iosLike) {
      setVisible(true);
      return;
    }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    const onInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
    };
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSHelp(true);
      return;
    }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch (_) {}
    setDeferredPrompt(null);
    setVisible(false);
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <>
      {/* Sticky install banner — mobile only */}
      <div
        className="sm:hidden fixed left-3 right-3 z-[45] rounded-2xl px-4 py-3 flex items-center gap-3"
        style={{
          bottom: 'calc(env(safe-area-inset-bottom,0px) + 96px)',
          backgroundColor: 'rgba(15, 42, 68, 0.94)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          border: '1px solid rgba(255,255,255,0.14)',
          boxShadow: '0 12px 30px rgba(0,0,0,0.35)',
        }}
        data-testid="pwa-install-banner"
      >
        <div
          className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
          style={{ backgroundColor: 'rgba(45,123,193,0.25)' }}
        >
          <Smartphone size={20} className="text-sky-200" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-semibold leading-tight">
            Installer l'app sur votre téléphone
          </p>
          <p className="text-white/70 text-[11px] leading-tight mt-0.5">
            Accès rapide, hors ligne — sans passer par le navigateur.
          </p>
        </div>
        <button
          type="button"
          onClick={handleInstall}
          className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-slate-900 bg-white shadow-sm active:scale-95 transition"
          data-testid="pwa-install-btn"
        >
          <Download size={14} />
          {isIOS ? 'Comment' : 'Installer'}
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          className="text-white/60 hover:text-white p-1"
          aria-label="Fermer"
          data-testid="pwa-install-dismiss"
        >
          <X size={16} />
        </button>
      </div>

      {/* iOS instructions modal */}
      {showIOSHelp && (
        <div
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
          onClick={() => setShowIOSHelp(false)}
          data-testid="ios-install-help"
        >
          <div
            className="w-full max-w-sm bg-white rounded-3xl p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold text-slate-900">Installer sur iPhone</h3>
              <button
                type="button"
                onClick={() => setShowIOSHelp(false)}
                className="text-slate-400 hover:text-slate-700"
              >
                <X size={20} />
              </button>
            </div>
            <ol className="space-y-3 text-sm text-slate-700">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-sky-100 text-sky-700 font-bold text-xs flex items-center justify-center">1</span>
                <span>Appuyez sur l'icône <Share size={14} className="inline text-sky-600 -mt-0.5" /> <strong>Partager</strong> en bas de Safari.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-sky-100 text-sky-700 font-bold text-xs flex items-center justify-center">2</span>
                <span>Faites défiler et sélectionnez <strong>« Sur l'écran d'accueil »</strong>.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-sky-100 text-sky-700 font-bold text-xs flex items-center justify-center">3</span>
                <span>Appuyez sur <strong>« Ajouter »</strong> en haut à droite. L'app apparaîtra comme une application classique.</span>
              </li>
            </ol>
            <button
              type="button"
              onClick={() => setShowIOSHelp(false)}
              className="mt-5 w-full rounded-full py-2.5 text-sm font-semibold text-white bg-sky-600 hover:bg-sky-700"
            >
              J'ai compris
            </button>
          </div>
        </div>
      )}
    </>
  );
}
